<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import VoiceNumbersAPI from 'dashboard/api/voiceNumbers';
import { formatDuration } from 'shared/helpers/timeHelper';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  voiceNumberId: { type: Number, required: true },
});

const { t } = useI18n();

const usage = ref(null);
const isFetching = ref(false);
const period = ref('7d');

const PERIODS = ['today', '7d', '30d', 'month'];

const line = computed(() => usage.value?.line || {});
const provider = computed(() => usage.value?.provider || {});
const daily = computed(() => provider.value.daily || []);

const money = (amount, digits = 2) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: provider.value.currency || 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount || 0);

const dayLabel = date =>
  new Date(`${date}T12:00:00`).toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
  });

// The four numbers an operation acts on. Everything else is detail below them.
const tiles = computed(() => [
  {
    key: 'calls',
    label: t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.CALLS'),
    value: line.value.totals?.calls ?? 0,
    hint: t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.CALLS_SPLIT', {
      inbound: line.value.totals?.inbound ?? 0,
      outbound: line.value.totals?.outbound ?? 0,
    }),
  },
  {
    key: 'answered',
    label: t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.ANSWERED'),
    value: line.value.totals?.answered ?? 0,
    hint: t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.ANSWER_RATE', {
      rate: line.value.answer_rate ?? 0,
    }),
  },
  {
    key: 'talk',
    label: t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.TALK_TIME'),
    value: formatDuration(line.value.duration?.total_seconds ?? 0),
    hint: t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.AVERAGE', {
      duration: formatDuration(line.value.duration?.average_seconds ?? 0),
    }),
  },
  {
    key: 'cost',
    label: t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.COST'),
    value: money(provider.value.total),
    hint: t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.BALANCE', {
      amount: money(provider.value.balance),
    }),
  },
]);

// Bars are read against the busiest day, not against zero-to-max-of-everything: the point is
// which day cost more than the others.
const peakPrice = computed(() =>
  Math.max(...daily.value.map(day => day.price), 0.0001)
);
const peakUsage = computed(() =>
  Math.max(...(provider.value.breakdown || []).map(row => row.price), 0.0001)
);

const hasChart = computed(() => daily.value.length > 1);

// The value has to be readable, and a chart this small has no room for an axis: the day under the
// cursor writes itself in the chart's own header.
const hoveredDay = ref(null);
const chartHeading = computed(() => {
  if (!hoveredDay.value) return t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.DAILY');
  return `${dayLabel(hoveredDay.value.date)} · ${money(hoveredDay.value.price, 4)}`;
});

const UNITS = {
  minutes: 'MINUTES',
  numbers: 'NUMBERS',
  recognitions: 'RECOGNITIONS',
};

const formatUsage = row => {
  const count = Math.round(row.usage);
  const unit = UNITS[row.unit];
  return unit
    ? t(`INBOX_MGMT.VOICE_CONFIGURATION.USAGE.UNIT.${unit}`, { count }, count)
    : `${count} ${row.unit}`;
};

const categoryLabel = category =>
  t(
    `INBOX_MGMT.VOICE_CONFIGURATION.USAGE.CATEGORY.${category
      .toUpperCase()
      .replace(/-/g, '_')}`
  );

const fetchUsage = async () => {
  isFetching.value = true;
  try {
    const { data } = await VoiceNumbersAPI.usage(props.voiceNumberId, {
      period: period.value,
    });
    usage.value = data;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.ERROR')
    );
  } finally {
    isFetching.value = false;
  }
};

watch(period, fetchUsage, { immediate: true });
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex flex-col gap-0.5">
        <span class="text-base font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.LABEL') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.DESCRIPTION') }}
        </span>
      </div>

      <div class="flex items-center gap-2">
        <Spinner v-if="isFetching" class="size-4 text-n-slate-11" />
        <div class="flex p-0.5 rounded-lg bg-n-alpha-2">
          <button
            v-for="option in PERIODS"
            :key="option"
            type="button"
            class="px-3 py-1 text-sm rounded-md"
            :class="
              period === option
                ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="period = option"
          >
            {{
              $t(
                `INBOX_MGMT.VOICE_CONFIGURATION.USAGE.PERIOD.${option.toUpperCase()}`
              )
            }}
          </button>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <div
        v-for="tile in tiles"
        :key="tile.key"
        class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <span class="text-sm text-n-slate-11">{{ tile.label }}</span>
        <span class="text-xl font-medium text-n-slate-12">
          {{ tile.value }}
        </span>
        <span class="text-xs text-n-slate-10">{{ tile.hint }}</span>
      </div>
    </div>

    <div
      v-if="hasChart"
      class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <span class="text-sm text-n-slate-11">{{ chartHeading }}</span>
      <div class="flex items-end h-24 gap-1" @mouseleave="hoveredDay = null">
        <div
          v-for="day in daily"
          :key="day.date"
          class="flex items-end justify-center flex-1 h-full group"
          @mouseenter="hoveredDay = day"
        >
          <div
            class="w-full max-w-8 rounded-t bg-n-blue-9 group-hover:bg-n-blue-10 min-h-0.5"
            :style="{ height: `${(day.price / peakPrice) * 100}%` }"
          />
        </div>
      </div>
      <div class="flex justify-between text-xs text-n-slate-10">
        <span>{{ dayLabel(daily[0].date) }}</span>
        <span>{{ dayLabel(daily[daily.length - 1].date) }}</span>
      </div>
    </div>

    <div
      v-if="provider.breakdown?.length"
      class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <span class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.WHERE') }}
      </span>
      <div class="flex flex-col gap-2">
        <div
          v-for="row in provider.breakdown"
          :key="row.category"
          class="flex items-center gap-3 text-sm"
        >
          <span class="w-48 truncate text-n-slate-12">
            {{ categoryLabel(row.category) }}
          </span>
          <span class="flex-1 h-2 rounded-full bg-n-alpha-2">
            <span
              class="block h-2 rounded-full bg-n-blue-9"
              :style="{ width: `${(row.price / peakUsage) * 100}%` }"
            />
          </span>
          <span class="w-24 text-right text-n-slate-11">
            {{ formatUsage(row) }}
          </span>
          <span class="w-20 text-right tabular-nums text-n-slate-12">
            {{ money(row.price) }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="line.agents?.length"
      class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <span class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.BY_AGENT') }}
      </span>
      <table class="w-full text-sm table-fixed">
        <tbody>
          <tr
            v-for="agent in line.agents"
            :key="agent.id"
            class="border-t border-n-weak first:border-t-0"
          >
            <td class="py-2 truncate text-n-slate-12">{{ agent.name }}</td>
            <td class="w-24 py-2 text-right tabular-nums text-n-slate-11">
              {{
                $t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.AGENT_CALLS', {
                  count: agent.calls,
                })
              }}
            </td>
            <td class="w-24 py-2 text-right tabular-nums text-n-slate-11">
              {{ formatDuration(agent.talk_seconds) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="mb-0 text-xs text-n-slate-10">
      {{ $t('INBOX_MGMT.VOICE_CONFIGURATION.USAGE.ACCOUNT_WIDE') }}
    </p>
  </div>
</template>
